import { Add, Cancel, Delete, ReplayOutlined } from '@mui/icons-material';

import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { useEffect, useState } from 'react';

import { useFieldArray, useFormContext } from 'react-hook-form';

import CountryOptionLabel from '../../../../../shared/components/mbui/CountryOptionLabel';

import IDOptionLabel, {
  getIDOptionLabel,
} from '../../../../../shared/components/mbui/IDOptionLabel';

import RHFAutocomplete from '../../../../../shared/components/react-hook-form/RHFAutocomplete';

import RHFDatePicker from '../../../../../shared/components/react-hook-form/RHFDatePicker';

import RHFNumber from '../../../../../shared/components/react-hook-form/RHFNumber';

import RHFTextField from '../../../../../shared/components/react-hook-form/RHFTextField';

import { useTheme } from '@mui/material/styles';

import { getPortsByCountry } from '../../../../../features/master-data';

import {
  getScenarioHighlightColor,
  scenarioCreatedBgColor,
  scenarioFieldHighlightSx,
} from '../../../../../shared/utils/scenarioHighlight';

import type { VesselFormValues } from './RHFVesselForm';

interface DestinationBlockProps {
  destinationIndex: number;

  countryOptions: { label: string; value: string }[];

  marketOptions: { label: string; value: string | number }[];

  mode: 'create' | 'edit' | 'group-edit';

  onRemove: () => void;

  canRemove: boolean;

  isCancelled?: boolean;

  isScenarioCancelled?: boolean;

  isScenarioCreated?: boolean;

  isExisting?: boolean;

  usedPorts?: string[];

  changedFields?: Set<string>;

  shipmentChangedFieldsMap?: Record<string, Set<string>>;

  readOnly?: boolean;

  /** Optional label suffix to distinguish same-port destination blocks. */
  voyageLabel?: string;

  onToggleCancel?: () => void;
}

const DestinationBlock = ({
  destinationIndex,

  countryOptions,

  marketOptions,

  mode,

  onRemove,

  canRemove,

  isCancelled = false,

  isScenarioCancelled = false,

  isScenarioCreated = false,

  isExisting = false,

  usedPorts = [],

  changedFields,

  shipmentChangedFieldsMap,

  readOnly = false,

  voyageLabel,

  onToggleCancel,
}: DestinationBlockProps) => {
  const theme = useTheme();

  const highlightSx = (djKey: string | string[]) => {
    const keys = Array.isArray(djKey) ? djKey : [djKey];

    return keys.some((k) => changedFields?.has(k))
      ? scenarioFieldHighlightSx(theme)
      : undefined;
  };

  const {
    control,

    watch,

    setValue,

    getValues,

    formState: { errors },
  } = useFormContext<VesselFormValues>();

  const { fields, append, remove } = useFieldArray({
    control,

    name: `destinations.${destinationIndex}.shipments` as const,
  });

  const [destinationPorts, setDestinationPorts] = useState<
    { label: string; value: string }[]
  >([]);

  const [isLoadingPorts, setIsLoadingPorts] = useState(false);

  const getFilteredMarketOptions = (shipmentIdx: number) => {
    const shipments = watch(
      `destinations.${destinationIndex}.shipments` as const
    ) as Array<{ market: string | number | null }> | undefined;

    const selected = (shipments ?? [])

      .map((s) => s?.market)

      .filter((v) => v !== null && v !== undefined) as Array<string | number>;

    const current = shipments?.[shipmentIdx]?.market ?? null;

    return marketOptions.filter(
      (op) => !selected.includes(op.value) || op.value === current
    );
  };

  // Watch this destination's country and fetch ports

  const watchCountry = watch(
    `destinations.${destinationIndex}.destinationCountry` as const
  );

  useEffect(() => {
    const code = watchCountry as string | null;

    let cancelled = false;

    const portFieldName =
      `destinations.${destinationIndex}.destinationPort` as any;

    const fetchPorts = async () => {
      if (!code) {
        setDestinationPorts([]);

        setValue(portFieldName, null as any);

        return;
      }

      setIsLoadingPorts(true);

      try {
        const portsData = await getPortsByCountry(code);

        if (cancelled) return;

        const options = portsData.map((v: any) => ({
          label: `${v.unLocode} ${v.name}`,

          value: v.unLocode,
        }));

        setDestinationPorts(options);

        // Only clear the port if the current value is not in the fetched list

        const currentPort = getValues(portFieldName);

        if (currentPort && !options.some((o: any) => o.value === currentPort)) {
          setValue(portFieldName, null as any);
        }
      } catch {
        if (cancelled) return;

        setDestinationPorts([]);

        setValue(portFieldName, null as any);
      } finally {
        if (!cancelled) setIsLoadingPorts(false);
      }
    };

    fetchPorts();

    return () => {
      cancelled = true;
    };
  }, [watchCountry, destinationIndex, setValue, getValues]);

  const showNoPortsMsg =
    Boolean(watchCountry) && !isLoadingPorts && destinationPorts.length === 0;

  const destErrors = (errors as any)?.destinations?.[destinationIndex];

  // In group-edit mode, existing destinations use cancel/restore toggle;

  // new destinations (not yet in DB) can still be removed.

  const showCancelToggle = mode === 'group-edit' && isExisting;

  const showDeleteButton =
    (mode === 'create' || (mode === 'group-edit' && !isExisting)) && canRemove;

  const highlightColor = getScenarioHighlightColor(theme);

  const cancelBorderColor = isScenarioCancelled ? highlightColor : 'error.main';

  return (
    <Box
      sx={{
        border: '1px solid',

        borderColor: isCancelled
          ? cancelBorderColor
          : isScenarioCreated || changedFields?.has('_cancelled')
            ? highlightColor
            : 'divider',

        borderRadius: 1,

        p: 2,

        position: 'relative',

        ...(isCancelled && {
          backgroundColor: 'action.disabledBackground',
        }),

        ...(isScenarioCreated &&
          !isCancelled && {
            backgroundColor: scenarioCreatedBgColor(theme),
          }),
      }}
    >
      <Stack direction='column' gap={2}>
        {/* Header */}

        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Stack direction='row' alignItems='center' gap={1}>
            <Typography variant='h6'>
              Destination {destinationIndex + 1}
              {voyageLabel ? ` (${voyageLabel})` : ''}
            </Typography>

            {isCancelled && (
              <Typography
                variant='body2'
                color={isScenarioCancelled ? highlightColor : 'error.main'}
                fontWeight='bold'
              >
                (Cancelled)
              </Typography>
            )}
          </Stack>

          <Stack direction='row' alignItems='center' gap={0.5}>
            {!readOnly && showCancelToggle && (
              <Tooltip
                title={
                  isCancelled ? 'Restore Destination' : 'Cancel Destination'
                }
              >
                <Button
                  size='small'
                  variant={isCancelled ? 'contained' : 'outlined'}
                  color={
                    isCancelled
                      ? 'primary'
                      : isScenarioCancelled
                        ? 'warning'
                        : 'error'
                  }
                  startIcon={
                    isCancelled ? (
                      <ReplayOutlined fontSize='small' />
                    ) : (
                      <Cancel fontSize='small' />
                    )
                  }
                  onClick={onToggleCancel}
                >
                  {isCancelled ? 'Restore' : 'Cancel'}
                </Button>
              </Tooltip>
            )}

            {!readOnly && showDeleteButton && (
              <IconButton color='error' size='small' onClick={onRemove}>
                <Delete fontSize='small' />
              </IconButton>
            )}
          </Stack>
        </Stack>

        <Divider />

        {/* Read-only overlay when cancelled */}

        <Box
          sx={{
            ...(isCancelled && {
              pointerEvents: 'none',

              opacity: 0.5,

              userSelect: 'none',
            }),
          }}
        >
          <Stack direction='column' gap={2}>
            {/* Country, Port & Latest ETA */}

            <Stack direction='row' gap={2}>
              <Box sx={{ flex: 1 }}>
                <RHFAutocomplete
                  name={`destinations.${destinationIndex}.destinationCountry`}
                  options={countryOptions}
                  sx={highlightSx('destinationCountry')}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option?.value === value?.value
                  }
                  getOptionLabel={getIDOptionLabel}
                  renderOption={(props: any, option: any) => (
                    <li {...props} key={option.value}>
                      <CountryOptionLabel
                        prefix={option.value}
                        label={option.label}
                        countryCode={option.value}
                      />
                    </li>
                  )}
                  placeholder='Select Destination Country'
                  label='Destination Country'
                  required
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <RHFAutocomplete
                  name={`destinations.${destinationIndex}.destinationPort`}
                  sx={highlightSx('destinationPort')}
                  options={destinationPorts.filter(
                    (p) => !usedPorts.includes(p.value)
                  )}
                  disabled={
                    !getValues(
                      `destinations.${destinationIndex}.destinationCountry` as any
                    )
                  }
                  isOptionEqualToValue={(option: any, value: any) =>
                    option?.value === value?.value
                  }
                  placeholder='Select Destination Port'
                  label='Destination Port'
                  required
                  textFieldProps={{
                    helperText: showNoPortsMsg
                      ? 'No ports available for selected country'
                      : undefined,
                  }}
                />
              </Box>

              <Box
                sx={{ flex: 1, ...highlightSx(['actualETA', 'initialETA']) }}
              >
                <RHFDatePicker
                  name={`destinations.${destinationIndex}.actualETA`}
                  label='Latest ETA'
                  required
                />
              </Box>
            </Stack>

            <RHFTextField
              name={`destinations.${destinationIndex}.generalRemarks`}
              label='General Remarks'
              sx={highlightSx('generalRemarks')}
              placeholder='Enter remarks for this destination'
              multiline
              rows={2}
            />

            {/* Shipments */}

            <Typography variant='subtitle1' fontWeight='bold'>
              Shipments ({fields.length})
            </Typography>

            {!!destErrors?.shipments?.message && (
              <Typography variant='body2' color='error' sx={{ mt: 0.5 }}>
                {destErrors.shipments.message}
              </Typography>
            )}

            {fields.length === 0 && (
              <Typography variant='body2' color='textSecondary'>
                No shipments added
              </Typography>
            )}

            {fields.length > 0 && (
              <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell />

                      <TableCell sx={{ minWidth: '250px' }}>
                        <Typography fontSize='14px'>Market</Typography>
                      </TableCell>

                      <TableCell>
                        <Typography fontSize='14px'>
                          First Shipping Line Offer
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography fontSize='14px'>
                          Adjusted PO Request
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography fontSize='14px'>Final Agreement</Typography>
                      </TableCell>

                      <TableCell>
                        <Typography fontSize='14px'>
                          Effective Shipped
                        </Typography>
                      </TableCell>

                      <TableCell />
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {fields.map((field, index) => {
                      const shipmentId = watch(
                        `destinations.${destinationIndex}.shipments.${index}._shipmentId` as any
                      ) as string | undefined;

                      const changedKeys: Set<string> =
                        (shipmentId &&
                          shipmentChangedFieldsMap?.[shipmentId]) ||
                        new Set<string>();

                      const numHighlight = (key: string) =>
                        changedKeys.has(key)
                          ? scenarioFieldHighlightSx(theme)
                          : undefined;

                      return (
                        <TableRow key={field.id}>
                          <TableCell sx={{ minWidth: '45px' }}>
                            <Typography variant='body2' color='textSecondary'>
                              {index + 1}.
                            </Typography>
                          </TableCell>

                          <TableCell sx={numHighlight('market')}>
                            <RHFAutocomplete
                              name={`destinations.${destinationIndex}.shipments.${index}.market`}
                              options={getFilteredMarketOptions(index)}
                              isOptionEqualToValue={(option: any, value: any) =>
                                option?.value === value?.value
                              }
                              getOptionLabel={getIDOptionLabel}
                              renderOption={(props: any, option: any) => (
                                <li {...props} key={option.value}>
                                  <IDOptionLabel
                                    id={option.value}
                                    label={option.label}
                                  />
                                </li>
                              )}
                              placeholder='Select Market'
                              label='Market'
                              required
                            />
                          </TableCell>

                          <TableCell
                            sx={numHighlight('firstShippingLineOffer')}
                          >
                            <RHFNumber
                              name={`destinations.${destinationIndex}.shipments.${index}.firstShippingLineOffer`}
                              inputProps={{ min: 0, max: 4000 }}
                              placeholder='0'
                            />
                          </TableCell>

                          <TableCell sx={numHighlight('adjustedPORequest')}>
                            <RHFNumber
                              name={`destinations.${destinationIndex}.shipments.${index}.adjustedPORequest`}
                              inputProps={{ min: 0, max: 4000 }}
                              placeholder='0'
                            />
                          </TableCell>

                          <TableCell sx={numHighlight('finalAgreement')}>
                            <RHFNumber
                              name={`destinations.${destinationIndex}.shipments.${index}.finalAgreement`}
                              inputProps={{ min: 0, max: 4000 }}
                              placeholder='0'
                            />
                          </TableCell>

                          <TableCell sx={numHighlight('effectiveShipped')}>
                            <RHFNumber
                              name={`destinations.${destinationIndex}.shipments.${index}.effectiveShipped`}
                              inputProps={{ min: 0, max: 4000 }}
                              placeholder='0'
                            />
                          </TableCell>

                          {!readOnly && (
                            <TableCell align='right'>
                              <IconButton
                                color='error'
                                size='small'
                                disabled={fields.length === 1}
                                onClick={() => remove(index)}
                              >
                                <Delete fontSize='small' />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!readOnly && !isCancelled && (
              <Button
                type='button'
                onClick={() =>
                  append({
                    market: '',

                    firstShippingLineOffer: 0,

                    adjustedPORequest: 0,

                    finalAgreement: 0,

                    effectiveShipped: 0,
                  })
                }
                variant='text'
                startIcon={<Add />}
                color='primary'
                fullWidth
              >
                Add Shipment
              </Button>
            )}
          </Stack>
        </Box>

        {/* end read-only overlay */}
      </Stack>
    </Box>
  );
};

export default DestinationBlock;
